import { useEffect, useState } from 'react';

/**
 * Dato histórico más cercano a `date` (YYYY-MM-DD) para el aviso "no hay datos aquí, pero sí en ...". El backend
 * (/api/historical-nearest) mira hacia los dos lados y devuelve el día con datos más próximo (con issue, siempre D-0).
 * Solo busca con `enabled` (la capa ya se sabe sin datos ese día) y una vez por capa + día + issues.
 * - layer: capa del backend ('dust_historical', 'lightnings_eu_025', 'rf_historical_v3', ...)
 * - hours: issues que ofrece el selector de esa capa ('01,13,19'), si los hay
 * - reload: cambia con el botón Retry para repetir una búsqueda que falló
 * Devuelve { nearest: { date, issueDate, hours, time } | null, searching }. nearest = null mientras busca, si no hay
 * datos cerca o si la búsqueda falló; searching = todavía buscando (el aviso ya sale y lo dice con SEARCHING_NEAREST)
 */
// De las horas de issue con datos ('01', '07', ...), la más próxima a `hour` (la de ahora si está; a igual distancia, la anterior)
export const closestHour = (hours, hour) => hours.reduce((best, h) => (Math.abs(h - hour) < Math.abs(best - hour) ? h : best), hours[0]);

// Segunda línea del aviso de "sin datos" mientras se busca
export const SEARCHING_NEAREST = 'Looking for the closest available data…';

// Sin límite, una búsqueda colgada dejaba el aviso con SEARCHING_NEAREST para siempre; pasado, cuenta como fallida
const TIMEOUT_MS = 30000;

const useNearestData = (layer, date, hours, enabled, reload = 0) => {
  const key = `${layer}|${date}|${hours}|${reload}`;
  const [result, setResult] = useState(null); // { key, found }
  const active = enabled && !!layer && !!date;

  useEffect(() => {
    if (!active || result?.key === key) return undefined;
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, TIMEOUT_MS);
    const params = new URLSearchParams({ layer, date, ...(hours && { hours }) });
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/historical-nearest?${params}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((found) => setResult({ key, found }))
      .catch((err) => { if (err.name !== 'AbortError' || timedOut) setResult({ key, found: null }); })
      .finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); controller.abort(); };
  }, [active, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = result?.key === key;
  return { nearest: ready ? result.found : null, searching: active && !ready };
};

export default useNearestData;
